/* eslint-disable react/no-array-index-key */
import React, { Fragment } from "react";
import { Switch, Redirect, Route } from "react-router-dom";
import Error404View from "./views/404/Error404View";
import AccountLogin from "./views/AccountLogin";
import AuthGuard from "./components/AuthGuard";
import LoginGuard from "./components/LoginGuard";
import DashboardLayout from "./views/DashboardLayout";
import Login from "./views/Auth/Login";
import Authenticate from "./views/Auth/Authenticate";
import EmailVerification from "./views/Auth/EmailVerification";
import {
  AUTHENTICATE_URL,
  LOGIN_URL,
  ERROR_URL,
  EMAIL_VERIFICATION_URL,
  ACCOUNT_KEY_URL,
  PID_URL,
  AI_SEARCH_URL,
  HISTORY_URL,
  VIEW_FILE_URL,
  DATA_EXPLORER_URL,
  DATA_MANAGEMENT_URL,
  ACCOUNT_URL,
  SAVED_URL,
  FEEDBACK_URL,
  WORKPLACE_DASHBOARD_URL,
  WORKPLACE_EVALS_URL,
  WORKPLACE_GENERAL_SETTINGS_URL,
  WORKPLACE_USERS_URL,
  WORKPLACE_ROLE_ACCESS_URL,
  WORKPLACE_INTEGRATIONS_URL,
  WORKPLACE_BILLING_AND_SUBSCRIPTION_URL,
  WORKPLACE_AUDIT_LOGS_URL,
  WORKPLACE_URL,
} from "./utils/routes";
import History from "./views/History/History";
import NewSession from "./views/AI_Search/NewSession/NewSession";
import AI_Search from "./views/AI_Search/AI_Search/AI_Search";
import ViewFile from "./views/ViewFile";
import DataExplorer from "./views/Data/DataExplorer/DataExplorer";
import DataManagement from "./views/Data/DataManagement/DataManagement";
import Account from "./views/Settings/Account/Account";
import Bookmarks from "./views/Settings/Bookmarks/Bookmarks";
import ViewFeedback from "./views/Settings/Feedback";
import Workplace from "./views/Settings/Workplace/Workplace";
import Dashboard from "./views/Settings/Workplace/Workplace/Dashboard/Dashboard";
import Evals from "./views/Settings/Workplace/Workplace/Evals/Evals";
import GeneralSettings from "./views/Settings/Workplace/Workplace/GeneralSettings/GeneralSettings";
import Users from "./views/Settings/Workplace/Management/Users/Users";
import RoleAccess from "./views/Settings/Workplace/Management/RoleAccess/RoleAccess";
import Integrations from "./views/Settings/Workplace/Management/Integrations/Integrations";
import BillingAndSubscription from "./views/Settings/Workplace/Administration/BillingAndSubscription/BillingAndSubscription";
import AuditLogs from "./views/Settings/Workplace/Administration/AuditLogs/AuditLogs";
import AdminGuard from "./components/AdminGuard";
import PIDMainView from "./views/PID/PIDMainView/PIDMainView";
import PIDSessionView from "./views/PID/PIDSessionView/PIDSessionView";

const routesConfig = [
  {
    exact: true,
    path: "/",
    component: () => <Redirect to={AI_SEARCH_URL} />,
  },
  {
    exact: true,
    path: ERROR_URL,
    component: () => <Error404View />,
  },
  ...(process.env.REACT_APP_ENV === "staging"
    ? [
        {
          exact: true,
          path: ACCOUNT_KEY_URL,
          guard: LoginGuard,
          component: () => <AccountLogin />,
        },
      ]
    : []),
  {
    exact: true,
    path: LOGIN_URL,
    guard: LoginGuard,
    component: () => <Login />,
  },
  {
    exact: true,
    path: EMAIL_VERIFICATION_URL,
    guard: LoginGuard,
    component: () => <EmailVerification />,
  },
  {
    exact: true,
    path: AUTHENTICATE_URL,
    component: () => <Authenticate />,
  },
  {
    path: "",
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: AI_SEARCH_URL,
        component: () => <NewSession />,
      },
      {
        exact: true,
        path: `${AI_SEARCH_URL}/:sessionId`,
        component: () => <AI_Search />,
      },
      {
        exact: true,
        path: PID_URL,
        component: () => <PIDMainView />,
      },
      {
        exact: true,
        path: `${PID_URL}/:sessionId`,
        component: () => <PIDSessionView />,
      },
      {
        exact: true,
        path: HISTORY_URL,
        component: () => <History />,
      },
      {
        exact: true,
        path: DATA_EXPLORER_URL,
        component: () => <DataExplorer />,
      },
      {
        exact: true,
        path: `${DATA_EXPLORER_URL}/:path*`,
        component: () => <DataExplorer />,
      },
      {
        exact: true,
        path: DATA_MANAGEMENT_URL,
        component: () => <DataManagement />,
      },
      {
        exact: true,
        path: `${VIEW_FILE_URL}/:fileId`,
        component: () => <ViewFile />,
      },
      {
        exact: true,
        path: ACCOUNT_URL,
        component: () => <Account />,
      },
      {
        exact: true,
        path: SAVED_URL,
        component: () => <Bookmarks />,
      },
      {
        exact: true,
        path: FEEDBACK_URL,
        component: () => <ViewFeedback />,
      },
      {
        path: WORKPLACE_URL,
        layout: Workplace,
        guard: AdminGuard,
        routes: [
          {
            exact: true,
            path: WORKPLACE_URL,
            component: () => <Redirect to={WORKPLACE_DASHBOARD_URL} />,
          },
          {
            exact: true,
            path: WORKPLACE_DASHBOARD_URL,
            component: () => <Dashboard />,
          },
          {
            exact: true,
            path: WORKPLACE_EVALS_URL,
            component: () => <Evals />,
          },
          {
            exact: true,
            path: WORKPLACE_GENERAL_SETTINGS_URL,
            component: () => <GeneralSettings />,
          },
          {
            exact: true,
            path: WORKPLACE_USERS_URL,
            component: () => <Users />,
          },
          {
            exact: true,
            path: WORKPLACE_ROLE_ACCESS_URL,
            component: () => <RoleAccess />,
          },
          {
            exact: true,
            path: WORKPLACE_INTEGRATIONS_URL,
            component: () => <Integrations />,
          },
          {
            exact: true,
            path: WORKPLACE_BILLING_AND_SUBSCRIPTION_URL,
            component: () => <BillingAndSubscription />,
          },
          {
            exact: true,
            path: WORKPLACE_AUDIT_LOGS_URL,
            component: () => <AuditLogs />,
          },
        ],
      },
    ],
  },
  {
    component: () => <Redirect to={ERROR_URL} />,
  },
];

const renderRoutes = (routes) => (
  <Switch>
    {routes.map((route, i) => {
      const Guard = route.guard || Fragment;
      const Layout = route.layout || Fragment;
      const Component = route.component;

      return (
        <Route
          key={i}
          path={route.path}
          exact={route.exact}
          render={(props) => (
            <Guard>
              <Layout>
                {route.routes ? (
                  renderRoutes(route.routes)
                ) : (
                  <Component {...props} />
                )}
              </Layout>
            </Guard>
          )}
        />
      );
    })}
  </Switch>
);

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
